/**
 * PrivacyLegalDocumentModal — legal copy (privacy / data deletion / terms) from the global menu.
 * Same BaseModal + bounded scroll sheet pattern as PrivacyAccountDeletionModal.
 */

import React, { useMemo } from 'react'
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  useWindowDimensions,
} from 'react-native'
import { X } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { useTheme } from '../../../src/theme/useTheme'
import { getLegalDocumentPageContent } from '../../../src/landing/helpers/legalDocumentContent.helper'
import type { LegalDocumentVariant } from '../../../src/landing/types/legalDocument.types'
import { shouldShowDocumentVersion } from '../../../src/landing/helpers/legalDocumentContent.helper'
import {
  LEGAL_DOCUMENT_VERSION,
  LEGAL_DOCUMENT_LAST_UPDATED,
} from '../../../src/landing/constants/legalDocumentVersion.constants'
import { computeMenuSheetLayout } from '../../../src/layout/helpers/menuSheetLayout.helper'
import { LanguageContext } from '../../../src/i18n'
import { BaseModal } from '../../ui/BaseModal'
import { getPrivacyLegalDocumentModalStyles } from './PrivacyLegalDocumentModal.styles'
import {
  PRIVACY_LEGAL_DOCUMENT_MODAL,
  PRIVACY_LEGAL_DOCUMENT_MODAL_CONTENT_TEST_ID,
  PRIVACY_LEGAL_DOCUMENT_MODAL_TEST_ID,
} from './privacyLegalDocumentModal.constants'

export interface PrivacyLegalDocumentModalProps {
  isVisible: boolean
  onDismiss: () => void
  variant: LegalDocumentVariant | null
  testID?: string
}

export const PrivacyLegalDocumentModal: React.FC<PrivacyLegalDocumentModalProps> = ({
  isVisible,
  onDismiss,
  variant,
  testID,
}) => {
  const theme = useTheme()
  const languageContext = React.useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('PrivacyLegalDocumentModal must be used within LanguageProvider')
  }
  const { t, language } = languageContext
  const { width: winW, height: winH } = useWindowDimensions()

  const layout = useMemo(
    () => computeMenuSheetLayout(winW, winH),
    [winW, winH]
  )

  const styles = useMemo(
    () => getPrivacyLegalDocumentModalStyles(theme, layout),
    [theme, layout]
  )

  const { title, sections } = useMemo(() => {
    if (!variant) {
      return { title: '', sections: [] as { title: string; paragraphs: readonly string[] }[] }
    }
    return getLegalDocumentPageContent(variant, t, language)
  }, [variant, t, language])

  const showWebHover = Platform.OS === 'web'
  const modalTestId = testID ?? PRIVACY_LEGAL_DOCUMENT_MODAL_TEST_ID

  if (!isVisible || !variant) {
    return null
  }

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={onDismiss}
      blurIntensity={PRIVACY_LEGAL_DOCUMENT_MODAL.BASE_MODAL_BLUR_INTENSITY}
      contentAlignment="center"
      contentContainerStyle={styles.modalOuter}
      testID={modalTestId}
      contentTestID={testID ? `${testID}-content` : PRIVACY_LEGAL_DOCUMENT_MODAL_CONTENT_TEST_ID}
    >
      <View style={styles.sheet} pointerEvents="box-none">
        <Pressable
          style={({ pressed, hovered }) => [
            styles.closeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.closeButtonPressed,
            hovered && showWebHover && styles.closeButtonHover,
          ]}
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel="Close"
          hitSlop={12}
        >
          <X size={22} color={theme.gray[500]} />
        </Pressable>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={Platform.OS !== 'web'}
          bounces
        >
          <Text style={styles.title} accessibilityRole="header">
            {title}
          </Text>

          {variant !== null && shouldShowDocumentVersion(variant) ? (
            <Text style={styles.versionLine}>
              {t.legal.documentVersionLine
                .replace('{date}', LEGAL_DOCUMENT_LAST_UPDATED)
                .replace('{version}', LEGAL_DOCUMENT_VERSION)}
            </Text>
          ) : null}

          {sections.map((section, sectionIndex) => {
            const isLast = sectionIndex === sections.length - 1
            return (
              <View
                key={section.title}
                style={[styles.section, isLast && styles.sectionLast]}
              >
                <Text style={styles.sectionTitle}>{section.title}</Text>
                {section.paragraphs.map((paragraph, index) => {
                  const isLastPara = index === section.paragraphs.length - 1
                  return (
                    <Text
                      key={`${section.title}-${index}`}
                      style={[styles.paragraph, isLastPara && styles.paragraphLast]}
                    >
                      {paragraph}
                    </Text>
                  )
                })}
              </View>
            )
          })}
        </ScrollView>
      </View>
    </BaseModal>
  )
}
